/**
 * The second review's per-team limits on the Review scans form (TRA-1569
 * patch 4): the people-queue size, the nightly cut and the good-flag
 * share. TrainLoop staff only; empty means the global number. The share
 * is a fraction on the API (0.2) and percent on the page (20), converted
 * here both ways. The repeat level was the fourth box until 2026-10-26:
 * a flag's level now comes from the judge's answers only.
 */

import type { DreamScanSettings, UpdateDreamScanRequest } from '@/api/generated/model'

export const LIMIT_NAMES = ['queueDepth', 'cutSize', 'goodShare'] as const
export type LimitName = (typeof LIMIT_NAMES)[number]

/** The three inputs as typed, so "050" and "" survive until a save. */
export type TeamLimitsDraft = Record<LimitName, string>

export const QUEUE_DEPTH_MAX = 500
export const CUT_SIZE_MAX = 5000

interface LimitRule {
  max: number
  wholeNumber: boolean
  /** Whole numbers on the page, or a percent with decimals. */
  what: string
}

// The share takes decimals: the API stores any fraction 0..1 (0.125 is
// 12.5%), and the page must accept every value the API can hold, or a
// stored share would fail here and block the Save for everyone.
const RULES: Record<LimitName, LimitRule> = {
  queueDepth: { max: QUEUE_DEPTH_MAX, wholeNumber: true, what: 'The people queue size' },
  cutSize: { max: CUT_SIZE_MAX, wholeNumber: true, what: 'The nightly cut' },
  goodShare: { max: 100, wholeNumber: false, what: 'The good-flag share' },
}

/** A fraction from the API as the percent text the box shows: 0.2 is "20", 0.005 is "0.5". */
export function percentText(fraction: number | null): string {
  if (fraction === null) return ''
  return String(Number((fraction * 100).toPrecision(12)))
}

/** The percent text as the fraction a save sends: "20" is 0.2, "0.5" is 0.005. */
export function fractionValue(percent: string): number | null {
  return percent.trim() === '' ? null : Number(percent) / 100
}

/** A whole-number box as a save sends it: null for empty. */
export function countValue(text: string): number | null {
  return text.trim() === '' ? null : Number(text)
}

export function limitsFromSettings(settings: DreamScanSettings): TeamLimitsDraft {
  return {
    queueDepth: settings.queue_depth === null ? '' : String(settings.queue_depth),
    cutSize: settings.cut_size === null ? '' : String(settings.cut_size),
    goodShare: percentText(settings.good_share),
  }
}

/** The global numbers as the boxes show them (the share in percent). */
export function globalLimitsText(settings: DreamScanSettings): TeamLimitsDraft {
  return {
    queueDepth: String(settings.global_queue_depth),
    cutSize: String(settings.global_cut_size),
    goodShare: percentText(settings.global_good_share),
  }
}

export function validateLimit(name: LimitName, text: string): string | null {
  if (text.trim() === '') return null
  const rule = RULES[name]
  const value = Number(text)
  const inRange = Number.isFinite(value) && value >= 0 && value <= rule.max
  if (rule.wholeNumber ? !Number.isInteger(value) || !inRange : !inRange) {
    const kind = rule.wholeNumber ? 'a whole number' : 'a number'
    return `${rule.what} must be ${kind} between 0 and ${String(rule.max)}.`
  }
  return null
}

export type LimitErrors = Record<LimitName, string | null>

export function validateLimits(draft: TeamLimitsDraft): LimitErrors {
  return {
    queueDepth: validateLimit('queueDepth', draft.queueDepth),
    cutSize: validateLimit('cutSize', draft.cutSize),
    goodShare: validateLimit('goodShare', draft.goodShare),
  }
}

export function limitsValid(errors: LimitErrors): boolean {
  return LIMIT_NAMES.every((name) => errors[name] === null)
}

/** The three fields as a save sends them; only a staff save includes them. */
export type TeamLimitsBody = Pick<UpdateDreamScanRequest, 'queue_depth' | 'cut_size' | 'good_share'>

export function limitsBody(draft: TeamLimitsDraft): TeamLimitsBody {
  return {
    queue_depth: countValue(draft.queueDepth),
    cut_size: countValue(draft.cutSize),
    good_share: fractionValue(draft.goodShare),
  }
}

/** One string per draft, compared as a save would send it, so "050" equals "50". */
export function limitsKey(draft: TeamLimitsDraft): string {
  return JSON.stringify(limitsBody(draft))
}
